import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { cn, Price } from '@divlab/divanui';
import { CSSTransition } from 'react-transition-group';

import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import Button from '@UI/Button';
import styles from './StickyBuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';
import type { ConfiguratorData } from '../..';
import type { CartModalInputProduct } from '@Types/Cart';

export interface StickyBuyButtonProps extends ButtonProps {
  className?: string;
  visible?: boolean;
  configurator: ConfiguratorData;
}

const StickyBuyButton: FC<StickyBuyButtonProps> = (props) => {
  const { className, visible = false, configurator, ...restProps } = props;
  const { configuration, parameterValues } = configurator;
  const page = PageProductStore.useData();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const { country } = useRequest();
  const isUZB = country === 'UZB';

  interface Parameter {
    id: number;
    count: number;
    parameter_id: number;
  }

  const parameters = useMemo<Parameter[]>(() => {
    if (!configuration.isCustom && configuration.isStandard) return [];
    return parameterValues
      .filter((val) => val.selected)
      .map((p) => ({
        id: p.value,
        count: p.count,
        parameter_id: p.parameterIds[0],
      }));
  }, [configuration.isCustom, configuration.isStandard, parameterValues]);

  const product = useMemo<CartModalInputProduct>(() => {
    return {
      image: configuration.image,
      shopProductId: configuration.id,
      parameterValues: parameters,
      quantity: 1,
      fabricValues: parameterValues
        .filter((val) => val.selected && val.type === 'fabric')
        .map((p) => ({
          fabric_id: p.value,
          parameter_id: p.parameterIds[0],
        })),
    };
  }, [configuration.id, configuration.image, parameterValues, parameters]);

  const handleClick = useCallback(() => {
    ModalWindows.open('Cart', {
      products: [
        {
          ...product,
          isModular: page.isModular,
          paymentTypeId: -1,
          isModifiedParameters: true,
        },
      ],
      analyticsLabel: 'Карточка товара',
    });
  }, [page.isModular, product]);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  return (
    <CSSTransition
      timeout={400}
      classNames={{
        enter: styles.enter,
        enterActive: styles.enterActive,
        enterDone: styles.enterDone,
        exit: styles.exit,
        exitActive: styles.exitActive,
        exitDone: styles.exitDone,
      }}
      unmountOnExit
      in={open}
    >
      <div className={cn(styles.inner, { [styles.UZB]: isUZB }, className)}>
        <Price className={styles.price} price={configuration.price.actual} />
        <Button
          {...restProps}
          wide
          size='l'
          className={cn(styles.buyButton, styles.sizeBig)}
          onClick={handleClick}
          data-testid='mobile-buy-button'
        >
          {t('ui.add-to-cart')}
        </Button>
      </div>
    </CSSTransition>
  );
};

export default memo(StickyBuyButton);
